import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationsApi, Notification, NotificationResponse } from '../lib/api/notifications';
import { useAuth } from '@/components/AuthProvider';
import { BusinessAccountContext } from '@/contexts/BusinessAccountContext';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { useChat } from '@/contexts/ChatContext';
import { useToast } from '@/hooks/use-toast';
import { friendlyMessage } from '@/lib/errors';
import { CURRENT_SUBSCRIPTION_KEY } from '@/hooks/useCurrentSubscription';
import {
  inAppAnnouncementOf,
  isForCurrentSurface,
  isInAppAnnouncementExpired,
  shouldSuppressToast,
} from '@/lib/notifications/inAppNotification';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: Error | null;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  markConversationAsReadLocal: (conversationId: string) => void;
  refetch: () => void;
}

export const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const businessAccountContext = useContext(BusinessAccountContext);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const selectedAccountId = businessAccountContext?.selectedAccountId || null;
  const { selectedWorkspaceId } = useWorkspaceContext();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsList, setNotificationsList] = useState<Notification[]>([]);
  const { socket, isConnected } = useChat();
  const currentScope = selectedWorkspaceId || 'no-workspace';
  const cacheKey = useMemo<[string, string]>(() => ['notifications', currentScope], [currentScope]);
  const businessRoomRef = useRef<string | null>(null);
  const [socketAuthenticated, setSocketAuthenticated] = useState(false);
  const latestScopeRef = useRef(currentScope);
  const seenTemplateStatusEventsRef = useRef<Map<string, number>>(new Map());
  const toastedNotificationIdsRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    latestScopeRef.current = currentScope;
  }, [currentScope]);

  if (false && !user) {
    const emptyContext: NotificationContextType = {
      notifications: [],
      unreadCount: 0,
      isLoading: false,
      error: null,
      markAsRead: async () => {},
      markAllAsRead: async () => {},
      deleteNotification: async () => {},
      markConversationAsReadLocal: () => {},
      refetch: () => {},
    };

    return (
      <NotificationContext.Provider value={emptyContext}>
        {children}
      </NotificationContext.Provider>
    );
  }

  const {
    data: notificationsData,
    isLoading,
    error,
    refetch,
  } = useQuery<NotificationResponse>({
    queryKey: cacheKey,
    queryFn: () =>
      notificationsApi.getAll({
        workspaceId: selectedWorkspaceId || undefined,
        orderBy: 'created_at',
        ascending: false,
        limit: 50,
      }),
    enabled: !!user,
    refetchInterval: false,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (notificationsData?.data) {
      setNotificationsList(notificationsData.data);
      setUnreadCount(notificationsData.unreadCount || 0);
    } else if (!notificationsData && !isLoading) {
      setNotificationsList([]);
      setUnreadCount(0);
    }
  }, [notificationsData, isLoading]);

  const notificationsListRef = useRef<Notification[]>([]);
  useEffect(() => {
    notificationsListRef.current = notificationsList;
  }, [notificationsList]);

  const mutateCache = useCallback(
    (
      scope: string | null | undefined,
      updater: (oldData?: NotificationResponse) => NotificationResponse | undefined
    ) => {
      const key: [string, string] = ['notifications', scope || 'no-account'];
      queryClient.setQueryData(key, (oldData?: NotificationResponse) => updater(oldData));
    },
    [queryClient]
  );

  const normalizeNotificationPayload = useCallback(
    (payload: any): Notification => {
      const createdAt = payload?.createdAt || payload?.created_at || new Date().toISOString();
      const updatedAt = payload?.updatedAt || payload?.updated_at || createdAt;
      const businessAccountId =
        payload?.businessAccountId ??
        payload?.business_account_id ??
        payload?.businessAccountID ??
        null;
      const data = payload?.data ?? payload?.metadata ?? null;
      const rawMessage = payload?.message || payload?.body || '';
      const errorCode = data && typeof data === 'object' && typeof data.code === 'string' ? data.code : undefined;
      const hasJobError = Boolean(errorCode || (data && typeof data === 'object' && data.error));
      const message = hasJobError && rawMessage
        ? friendlyMessage({ code: errorCode, error: rawMessage }, rawMessage)
        : rawMessage;

      return {
        id:
          payload?.id ||
          payload?.notificationId ||
          payload?.notification_id ||
          payload?.messageId ||
          `notification-${Date.now()}-${Math.random()}`,
        type: payload?.type || payload?.notificationType || 'GENERAL',
        title: payload?.title || payload?.message || 'Notification',
        message,
        data,
        isRead: payload?.isRead ?? payload?.is_read ?? false,
        createdAt,
        updatedAt,
        businessAccountId,
        workspaceId: payload?.workspaceId ?? payload?.workspace_id ?? null,
        organizationId: payload?.organizationId ?? payload?.organization_id ?? null,
        userId: payload?.userId ?? payload?.user_id ?? user?.id,
      };
    },
    [user?.id]
  );

  useEffect(() => {
    if (!socket || !isConnected || !user) return;

    const handleNotification = (payload: any) => {
      const normalized = normalizeNotificationPayload(payload);

      const incomingAnnouncement = inAppAnnouncementOf(normalized);
      if (incomingAnnouncement && !isForCurrentSurface(incomingAnnouncement)) {
        return;
      }
      if (incomingAnnouncement && isInAppAnnouncementExpired(incomingAnnouncement)) {
        return;
      }
      const targetScope = normalized.workspaceId || latestScopeRef.current;

      mutateCache(targetScope, (oldData) => {
        const existingNotifications = oldData?.data || [];
        const updatedData = [
          normalized,
          ...existingNotifications.filter((notification) => notification.id !== normalized.id),
        ].slice(0, 50);

        if (!oldData) {
          return {
            success: true,
            data: updatedData,
            unreadCount: normalized.isRead ? 0 : 1,
          };
        }

        return {
          ...oldData,
          data: updatedData,
        };
      });

      if (targetScope === latestScopeRef.current) {
        setNotificationsList((prev) => {
          const updated = [
            normalized,
            ...prev.filter((notification) => notification.id !== normalized.id),
          ].slice(0, 50);
          return updated;
        });

        if (!normalized.isRead) {
          setUnreadCount((prev) => prev + 1);
        }
      }

      const NOISY_TOAST_SKIP_TYPES = new Set<string>([
        'MESSAGE_RECEIVED',
        'CONVERSATION_UPDATED',
        'NEW_MESSAGE',
        'TYPING',
      ]);
      const shouldToast = (() => {
        if (!normalized.title && !normalized.message) return false;
        if (NOISY_TOAST_SKIP_TYPES.has(String(normalized.type).toUpperCase())) {
          return false;
        }
        if (shouldSuppressToast(normalized.data)) {
          return false;
        }
        if (incomingAnnouncement) {
          return false;
        }
        if (normalized.workspaceId && targetScope !== latestScopeRef.current) {
          return false;
        }
        const now = Date.now();
        const lastSeen = toastedNotificationIdsRef.current.get(normalized.id) || 0;
        if (now - lastSeen < 5000) return false;
        toastedNotificationIdsRef.current.set(normalized.id, now);
        if (toastedNotificationIdsRef.current.size > 250) {
          toastedNotificationIdsRef.current.clear();
        }
        return true;
      })();

      if (shouldToast) {
        const rawType = String(normalized.type || '').toLowerCase();
        const isFailure =
          rawType === 'error' ||
          rawType.endsWith('_failed') ||
          rawType.includes('failure');
        toast({
          title: normalized.title,
          description: normalized.message || undefined,
          variant: isFailure ? 'destructive' : 'default',
        });
      }

      try {
        window.dispatchEvent(
          new CustomEvent('wenext:notification-received', {
            detail: {
              type: normalized.type,
              data: normalized.data,
              contactId:
                (normalized.data as any)?.contactId ||
                (normalized.data as any)?.contact_id ||
                null,
              reminderId:
                (normalized.data as any)?.reminderId ||
                (normalized.data as any)?.reminder_id ||
                null,
            },
          })
        );
      } catch {
      }
    };

    const handleTemplateStatusUpdate = (payload: any) => {
      const businessAccountId =
        payload?.businessAccountId ?? payload?.business_account_id ?? payload?.businessAccountID ?? null;
      const status = payload?.status;

      if (businessAccountId && businessAccountId !== selectedAccountId) return;
      const targetScope = businessAccountId || latestScopeRef.current;

      if (status !== 'APPROVED') return;

      const templateId = payload?.templateId ?? payload?.template_id ?? '';
      const templateName = payload?.templateName ?? payload?.template_name ?? templateId ?? 'template';
      const dedupeKey = [targetScope, templateId, status].filter(Boolean).join(':');
      const now = Date.now();
      const lastSeen = seenTemplateStatusEventsRef.current.get(dedupeKey) || 0;
      if (now - lastSeen < 1500) return;
      seenTemplateStatusEventsRef.current.set(dedupeKey, now);

      if (seenTemplateStatusEventsRef.current.size > 250) {
        seenTemplateStatusEventsRef.current.clear();
      }

      if (businessAccountId) {
        queryClient.invalidateQueries({ queryKey: ['meta-templates', businessAccountId] });
      }

      if (businessAccountId && templateId) {
        queryClient.invalidateQueries({ queryKey: ['meta-template', templateId, businessAccountId] });
      }

      toast({ variant: "success",
        title: 'Template approved',
        description: `Template "${templateName}" is approved and ready to use.`,
      });
    };

    const handleNotificationCountUpdate = (payload: any) => {
      const payloadWorkspaceId =
        payload?.workspaceId ?? payload?.workspace_id ?? null;
      const scope = payloadWorkspaceId || 'no-workspace';
      const unreadValue =
        typeof payload?.unreadCount === 'number'
          ? payload.unreadCount
          : typeof payload?.unread_count === 'number'
          ? payload.unread_count
          : undefined;

      if (typeof unreadValue !== 'number') {
        return;
      }

      mutateCache(payloadWorkspaceId, (oldData) => {
        if (!oldData) {
          return {
            success: true,
            data: [],
            unreadCount: unreadValue,
          };
        }

        return {
          ...oldData,
          unreadCount: unreadValue,
        };
      });

      if (scope === latestScopeRef.current) {
        setUnreadCount(unreadValue);
      }
    };

    const handleEntitlementUpdated = () => {
      queryClient.invalidateQueries({ queryKey: ['organization-entitlements'] });
      queryClient.invalidateQueries({ queryKey: CURRENT_SUBSCRIPTION_KEY });
    };

    const handleIncomingMessage = (data: any) => {
      const isInbound = data.isInbound || data.is_inbound || data.message?.is_inbound || data.message?.isInbound;
      const type = data.type || data.message?.type;

      if (!isInbound && type !== 'new_message') return;
      if (type === 'messages_read' || type === 'status_update') return;

      const msg = data.message || data;
      const conversationId = data.conversationId || msg.conversationId || msg.conversation_id;
      const messageId = data.messageId || msg.messageId || msg.id;
      const contactName = msg.contactName || msg.contact_name || msg.senderPhone || msg.sender_phone || 'Unknown';
      const content = msg.content || msg.content_text || msg.messagePreview || '';
      const businessAccountId = data.businessAccountId || msg.businessAccountId || msg.business_account_id;

      if (!conversationId) return;

      const dedupeId = `ws-msg-${messageId || conversationId}-${Date.now()}`;

      const wsNotification: Notification = {
        id: dedupeId,
        type: 'MESSAGE_RECEIVED',
        title: 'New Message',
        message: `New message from ${contactName}: ${content}`.substring(0, 200),
        data: { conversationId, contactName, messageId, messagePreview: content },
        isRead: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        businessAccountId: businessAccountId || null,
        workspaceId: selectedWorkspaceId || null,
        organizationId: null,
        userId: user?.id,
      };

      const belongsToCurrentWorkspace =
        !businessAccountId || businessAccountId === selectedAccountId;
      if (belongsToCurrentWorkspace) {
        setNotificationsList((prev) => {
          const isDuplicate = prev.some(n =>
            n.data?.conversationId === conversationId &&
            n.type === 'MESSAGE_RECEIVED' &&
            Math.abs(new Date(n.createdAt).getTime() - Date.now()) < 10000
          );
          if (isDuplicate) return prev;
          return [wsNotification, ...prev].slice(0, 50);
        });
        setUnreadCount((prev) => prev + 1);
      }
    };

    const handleNewMessageForNotification = (data: any) => {
      const msg = data.message || data;
      const isInbound = msg.is_inbound || msg.isInbound;
      if (isInbound) handleIncomingMessage({ ...msg, ...data, isInbound: true });
    };

    const handleConversationUpdatedForNotification = (data: any) => {
      if (data.type === 'new_message' && (data.isInbound || data.is_inbound)) {
        handleIncomingMessage(data);
      }
    };

    let messagingUsageDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    const handleOutboundMessageForUsage = (data: any) => {
      const msg = data.message || data;
      const isInbound = msg.is_inbound || msg.isInbound;
      if (isInbound) return;
      if (messagingUsageDebounceTimer) clearTimeout(messagingUsageDebounceTimer);
      messagingUsageDebounceTimer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['messaging-usage'] });
      }, 3000);
    };

    socket.on('notification', handleNotification);
    socket.on('notification_count_update', handleNotificationCountUpdate);
    socket.on('template_status_update', handleTemplateStatusUpdate);
    socket.on('entitlement_updated', handleEntitlementUpdated);
    socket.on('new-message', handleNewMessageForNotification);
    socket.on('new-message', handleOutboundMessageForUsage);
    socket.on('conversation_updated', handleConversationUpdatedForNotification);

    return () => {
      if (messagingUsageDebounceTimer) clearTimeout(messagingUsageDebounceTimer);
      socket.off('notification', handleNotification);
      socket.off('notification_count_update', handleNotificationCountUpdate);
      socket.off('template_status_update', handleTemplateStatusUpdate);
      socket.off('entitlement_updated', handleEntitlementUpdated);
      socket.off('new-message', handleNewMessageForNotification);
      socket.off('new-message', handleOutboundMessageForUsage);
      socket.off('conversation_updated', handleConversationUpdatedForNotification);
    };
  }, [socket, isConnected, user, mutateCache, normalizeNotificationPayload, queryClient, toast, selectedAccountId, selectedWorkspaceId]);

  useEffect(() => {
    if (!socket) return;
    const onAuthenticated = () => setSocketAuthenticated(true);
    const onDisconnect = () => setSocketAuthenticated(false);
    socket.on('authenticated', onAuthenticated);
    socket.on('disconnect', onDisconnect);
    return () => {
      socket.off('authenticated', onAuthenticated);
      socket.off('disconnect', onDisconnect);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket || !isConnected || !socketAuthenticated) {
      if (businessRoomRef.current) {
        businessRoomRef.current = null;
      }
      return;
    }

    const nextRoom = selectedAccountId ? `business-account-${selectedAccountId}` : null;

    if (businessRoomRef.current && businessRoomRef.current !== nextRoom) {
      socket.emit('leave_room', { roomId: businessRoomRef.current });
      businessRoomRef.current = null;
    }

    if (nextRoom && businessRoomRef.current !== nextRoom) {
      socket.emit('join_room', { roomId: nextRoom });
      businessRoomRef.current = nextRoom;
    }

    if (!nextRoom && businessRoomRef.current) {
      socket.emit('leave_room', { roomId: businessRoomRef.current });
      businessRoomRef.current = null;
    }

    return () => {
      if (businessRoomRef.current) {
        socket.emit('leave_room', { roomId: businessRoomRef.current });
        businessRoomRef.current = null;
      }
    };
  }, [socket, isConnected, socketAuthenticated, selectedAccountId]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      if (!user) return;

      if (typeof id === 'string' && id.startsWith('ws-msg-')) {
        const wasUnreadPseudo = notificationsListRef.current.some(
          (n) => n.id === id && !n.isRead
        );
        setNotificationsList((prev) =>
          prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
        );
        if (wasUnreadPseudo) {
          setUnreadCount((prev) => Math.max(0, prev - 1));
        }
        return;
      }

      const existing = notificationsListRef.current.find((n) => n.id === id);
      if (existing && existing.isRead) return;
      const wasUnread = Boolean(existing && !existing.isRead);

      setNotificationsList((prev) =>
        prev.map((notification) =>
          notification.id === id ? { ...notification, isRead: true } : notification
        )
      );

      if (wasUnread) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      mutateCache(selectedWorkspaceId, (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: oldData.data.map((notification) =>
            notification.id === id ? { ...notification, isRead: true } : notification
          ),
          unreadCount: wasUnread
            ? Math.max(0, (oldData.unreadCount || 0) - 1)
            : oldData.unreadCount,
        };
      });

      await notificationsApi.markAsRead(id);
    } catch (error) {
    }
  }, [mutateCache, selectedWorkspaceId, user]);

  const markAllAsRead = useCallback(async () => {
    try {
      if (!user) return;
      await notificationsApi.markAllAsRead({
        workspaceId: selectedWorkspaceId || undefined,
      });

      setNotificationsList((prev) =>
        prev.map((notification) => ({
          ...notification,
          isRead: true,
        }))
      );

      setUnreadCount(0);

      mutateCache(selectedWorkspaceId, (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          data: oldData.data.map((notification) => ({
            ...notification,
            isRead: true,
          })),
          unreadCount: 0,
        };
      });
    } catch (error) {
    }
  }, [mutateCache, selectedWorkspaceId, user]);

  const deleteNotification = useCallback(async (id: string) => {
    try {
      if (!user) return;
      await notificationsApi.delete(id);

      let removedUnread = false;
      setNotificationsList((prev) =>
        prev.filter((notification) => {
          if (notification.id === id && !notification.isRead) {
            removedUnread = true;
          }
          return notification.id !== id;
        })
      );

      if (removedUnread) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      mutateCache(selectedWorkspaceId, (oldData) => {
        if (!oldData) return oldData;

        const notificationToDelete = oldData.data.find((notification) => notification.id === id);
        const wasUnread = notificationToDelete ? !notificationToDelete.isRead : false;

        return {
          ...oldData,
          data: oldData.data.filter((notification) => notification.id !== id),
          unreadCount: wasUnread
            ? Math.max(0, (oldData.unreadCount || 0) - 1)
            : oldData.unreadCount,
        };
      });
    } catch (error) {
    }
  }, [mutateCache, selectedWorkspaceId, user]);

  const markConversationAsReadLocal = useCallback((conversationId: string) => {
    if (!conversationId) return;

    const matching = notificationsListRef.current.filter(
      (n) =>
        !n.isRead &&
        n.type === 'MESSAGE_RECEIVED' &&
        (n.data as any)?.conversationId === conversationId
    );
    if (matching.length === 0) return;

    const matchingSet = new Set(matching.map((n) => n.id));

    setNotificationsList((prev) =>
      prev.map((n) => (matchingSet.has(n.id) ? { ...n, isRead: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - matching.length));

    mutateCache(selectedWorkspaceId, (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        data: oldData.data.map((n) =>
          matchingSet.has(n.id) ? { ...n, isRead: true } : n
        ),
        unreadCount: Math.max(0, (oldData.unreadCount || 0) - matching.length),
      };
    });
  }, [mutateCache, selectedWorkspaceId]);

  const value = useMemo<NotificationContextType>(() => ({
    notifications: notificationsList,
    unreadCount,
    isLoading,
    error: error as Error | null,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    markConversationAsReadLocal,
    refetch,
  }), [notificationsList, unreadCount, isLoading, error, markAsRead, markAllAsRead, deleteNotification, markConversationAsReadLocal, refetch]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
